import React, { Fragment } from "react";

import { twMerge } from "tailwind-merge";

import { cellBaseClassName } from "src/dataTable/ResultCell";
import {
  buildOpenStringValueCallback,
  isLargeStringValue,
  MAX_STRING_VALUE_LENGTH_TOOLTIP,
  trimLargeString,
} from "src/dataTable/TableUtils";
import { CellContent, isReactNode } from "src/dataTable/types";
import { Tooltip } from "src/design-system/Tooltip";
import { speakPythonPrimitive } from "src/utils/speakPython";

type Props<T> = {
  cell: CellContent<T> | React.ReactElement;
  className?: string;
  classNameOverrides?: string;
  withTooltip?: boolean;
  suffix?: React.ReactNode;
};

export const RawCell = <T,>({
  cell,
  className = cellBaseClassName,
  classNameOverrides,
  suffix,
  withTooltip = false,
}: Props<T>) => {
  const value = (() => {
    if (isReactNode(cell)) {
      return cell;
    }

    const val = cell.getValue();

    return typeof val === "boolean"
      ? speakPythonPrimitive(val.toString())
      : speakPythonPrimitive(val);
  })() as React.ReactNode;

  const renderedValue = isLargeStringValue(value)
    ? trimLargeString(value)
    : value;

  const Wrapper = withTooltip
    ? ({ children }: { children: React.ReactNode }) => (
        <Tooltip
          body={isLargeStringValue(value) ? renderedValue : undefined}
          disabled={!(value !== undefined && value !== "")}
          footerAction={
            isLargeStringValue(value)
              ? {
                  text: MAX_STRING_VALUE_LENGTH_TOOLTIP,
                  onClick: buildOpenStringValueCallback(value),
                }
              : undefined
          }
          placement="top"
          title={isLargeStringValue(value) ? undefined : renderedValue}
          asChild
        >
          {children}
        </Tooltip>
      )
    : Fragment;

  return (
    <Wrapper>
      <div
        className={twMerge(
          "flex flex-row items-center justify-between gap-x-0.5 pl-2",
          className,
          classNameOverrides,
        )}
      >
        {Boolean(suffix) || typeof value === "string" ? (
          <>
            <div className="flex-1 truncate">{renderedValue}</div>
            {suffix}
          </>
        ) : (
          value
        )}
      </div>
    </Wrapper>
  );
};
